// engagement-components/ROIDashboard.jsx
// Full ROI attribution — 6 conversion types including phone/email sales attribution

import React, { useState, useEffect, useRef } from "react";
import {
  Row, Col, Card, Table, Badge, Button, Form, Spinner,
  ProgressBar, Modal, Alert, InputGroup, Tabs, Tab,
} from "react-bootstrap";
import {
  FaRupeeSign, FaArrowUp, FaArrowDown, FaChartLine, FaMousePointer,
  FaCheckCircle, FaCoins, FaTrophy, FaPhone, FaEnvelope,
  FaFileAlt, FaPoll, FaEdit, FaLink, FaEye, FaFilter,
} from "react-icons/fa";
import axios from "axios";
import dayjs from "dayjs";
import { apiUrl, authHeaders, SAMPLE } from "./config";
import { RightModal, RSelect, useNotify, DownloadMenu, EngagementDatePicker } from "./shared";
import moment from "moment";

// ─── Conversion type definitions ──────────────────────────────────────────────
export const CONVERSION_TYPES = [
  {
    id:     "link_click",
    label:  "Link click",
    icon:   <FaMousePointer />,
    color:  "primary",
    desc:   "Guest tapped {{link}} in the WhatsApp / SMS message",
    auto:   true,
  },
  {
    id:     "page_open",
    label:  "Offer page opened",
    icon:   <FaEye />,
    color:  "info",
    desc:   "Guest opened the landing page / offer page",
    auto:   true,
  },
  {
    id:     "feedback_submitted",
    label:  "Feedback submitted",
    icon:   <FaPoll />,
    color:  "success",
    desc:   "Guest completed a feedback survey via the link",
    auto:   true,
  },
  {
    id:     "booking_confirmed",
    label:  "Booking confirmed",
    icon:   <FaFileAlt />,
    color:  "success",
    desc:   "Guest clicked Book Now on the offer page and booking was confirmed",
    auto:   true,
  },
  {
    id:     "phone_sale",
    label:  "Phone sale",
    icon:   <FaPhone />,
    color:  "warning",
    desc:   "Guest called and made a purchase — staff marks as converted with amount",
    auto:   false,
  },
  {
    id:     "email_sale",
    label:  "Email / walk-in sale",
    icon:   <FaEnvelope />,
    color:  "warning",
    desc:   "Guest came in / emailed after receiving the message — staff attributes the sale",
    auto:   false,
  },
];

// ─── Sample data ───────────────────────────────────────────────────────────────
const SAMPLE_ROI = {
  summary: {
    totalRevenue:        284500,
    totalCreditCost:     4992,    // credits × ₹0.4 per credit
    roi:                 5595,    // %
    avgRevenuePerSend:   22.8,
    totalSent:           12480,
    totalClicks:         4551,
    totalConversions:    312,
    conversionRate:      2.5,
    clickRate:           36.5,
    byType: {
      link_click:          4551,
      page_open:           3820,
      feedback_submitted:  420,
      booking_confirmed:   189,
      phone_sale:          88,
      email_sale:          35,
    },
  },
  campaigns: [
    {
      id: "c1", name: "New Year Anniversary Special", eventType: "anniversary",
      sent: 520, delivered: 498, clicks: 210, conversions: 38,
      revenue: 82000, credits: 1040, revenueGoal: 80000,
      status: "Completed", date: "2025-01-01",
      conversionBreakdown: { link_click: 210, page_open: 180, feedback_submitted: 12, booking_confirmed: 22, phone_sale: 12, email_sale: 4 },
    },
    {
      id: "c2", name: "Birthday VIP Offer", eventType: "birthday",
      sent: 340, delivered: 328, clicks: 168, conversions: 29,
      revenue: 48500, credits: 680, revenueGoal: 50000,
      status: "Completed", date: "2025-01-05",
      conversionBreakdown: { link_click: 168, page_open: 142, feedback_submitted: 8, booking_confirmed: 15, phone_sale: 10, email_sale: 4 },
    },
    {
      id: "c3", name: "Weekend Spa Package", eventType: "one-time",
      sent: 680, delivered: 648, clicks: 201, conversions: 34,
      revenue: 34200, credits: 680, revenueGoal: 30000,
      status: "Active", date: "2025-01-10",
      conversionBreakdown: { link_click: 201, page_open: 168, feedback_submitted: 5, booking_confirmed: 18, phone_sale: 9, email_sale: 7 },
    },
    {
      id: "c4", name: "Check-out Follow-up", eventType: "checkout",
      sent: 1240, delivered: 1186, clicks: 390, conversions: 52,
      revenue: 28900, credits: 1240, revenueGoal: 25000,
      status: "Active", date: "2025-01-12",
      conversionBreakdown: { link_click: 390, page_open: 320, feedback_submitted: 215, booking_confirmed: 0, phone_sale: 28, email_sale: 24 },
    },
  ],
  // Manual conversions — phone / email sales attributed to campaign
  manualConversions: [
    { id: "mc1", campaignId: "c1", guestName: "Rahul Sharma", phone: "+91 98765 43210", type: "phone_sale", amount: 8200, note: "Called to book New Year package for 2", date: "2025-01-05", staffName: "Anita R." },
    { id: "mc2", campaignId: "c1", guestName: "Priya Mehta",  phone: "+91 87654 32109", type: "phone_sale", amount: 6499, note: "Booked 1 couple package", date: "2025-01-06", staffName: "Rahul S." },
    { id: "mc3", campaignId: "c2", guestName: "Vikram Singh", phone: "+91 54321 09876", type: "email_sale", amount: 4200, note: "Walked in with birthday offer screenshot", date: "2025-01-07", staffName: "Sonal M." },
  ],
};

const EVENT_ICON = { anniversary: "💍", birthday: "🎂", "one-time": "📣", checkin: "🏨", checkout: "👋" };
const statusVariant = (s) => ({ Active: "success", Completed: "primary", Scheduled: "warning", Draft: "secondary" }[s] || "secondary");

// ─── Manual Conversion Modal ───────────────────────────────────────────────────
const ManualConversionModal = ({ show, onHide, campaigns, onSaved }) => {
  const [form, setForm] = useState({
    campaignId: "", guestName: "", phone: "", email: "",
    type: "phone_sale", amount: "", note: "", staffName: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");

  const save = async () => {
    if (!form.campaignId) { setError("Select a campaign."); return; }
    if (!form.guestName)  { setError("Guest name is required."); return; }
    if (!form.amount)     { setError("Sale amount is required."); return; }
    setSaving(true);
    try {
      await axios.post(`${apiUrl}/feedback/campaigns/roi/overview`, {
        ...form, amount: Number(form.amount), date: new Date().toISOString(), manual: true,
      }, authHeaders());
    } catch { }
    onSaved({ ...form, id: `mc${Date.now()}`, date: new Date().toISOString(), amount: Number(form.amount) });
    setSaving(false);
    onHide();
    setForm({ campaignId: "", guestName: "", phone: "", email: "", type: "phone_sale", amount: "", note: "", staffName: "" });
  };

  return (
    <Modal show={show} onHide={onHide} centered size="md" aria-label="Log Manual Conversion">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaEdit className="me-2 text-warning" />Log Manual Conversion
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        <div className="alert alert-info py-2 mb-3" style={{ fontSize: "0.82rem" }}>
          <i className="fas fa-info-circle me-2"></i>
          Use this when a guest <strong>called</strong>, <strong>walked in</strong>, or <strong>emailed</strong> after receiving
          your campaign message — and made a purchase. This attributes the revenue to the campaign.
        </div>
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}

        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold">Campaign</Form.Label>
          <Form.Select value={form.campaignId} onChange={(e) => setForm((p) => ({ ...p, campaignId: e.target.value }))}>
            <option value="">Select campaign...</option>
            {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Form.Select>
        </Form.Group>

        <Row className="g-3 mb-3">
          <Col xs={6}>
            <Form.Label className="fw-semibold">Guest Name</Form.Label>
            <Form.Control value={form.guestName} onChange={(e) => setForm((p) => ({ ...p, guestName: e.target.value }))} placeholder="Rahul Sharma" />
          </Col>
          <Col xs={6}>
            <Form.Label className="fw-semibold">Phone / Email</Form.Label>
            <Form.Control value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} placeholder="+91 98765..." />
          </Col>
        </Row>

        <Row className="g-3 mb-3">
          <Col xs={6}>
            <Form.Label className="fw-semibold">Conversion Type</Form.Label>
            <Form.Select value={form.type} onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))}>
              <option value="phone_sale">📞 Phone sale</option>
              <option value="email_sale">✉️ Email / walk-in sale</option>
            </Form.Select>
          </Col>
          <Col xs={6}>
            <Form.Label className="fw-semibold">Sale Amount (₹)</Form.Label>
            <InputGroup>
              <InputGroup.Text>₹</InputGroup.Text>
              <Form.Control type="number" min="0" value={form.amount}
                onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))}
                placeholder="6499" />
            </InputGroup>
          </Col>
        </Row>

        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold">Note</Form.Label>
          <Form.Control value={form.note} onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
            placeholder="e.g. Called to book anniversary dinner for 2 couples" />
        </Form.Group>

        <Form.Group>
          <Form.Label className="fw-semibold">Staff Name (who logged this)</Form.Label>
          <Form.Control value={form.staffName} onChange={(e) => setForm((p) => ({ ...p, staffName: e.target.value }))} placeholder="Your name" />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancel</Button>
        <Button variant="warning" onClick={save} disabled={saving} className="px-4">
          {saving ? <Spinner size="sm" animation="border" className="me-1" /> : <FaEdit className="me-1" />}
          Log Conversion
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ─── Main ROIDashboard ─────────────────────────────────────────────────────────
const ROIDashboard = () => {
  const [data,           setData]           = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [dateRange,      setDateRange]      = useState({
    start: moment().subtract(29, "days").startOf("day"),
    end:   moment().endOf("day"),
  });
  const [sortField,      setSortField]      = useState("revenue");
  const [sortDir,        setSortDir]        = useState("desc");
  const [selected,       setSelected]       = useState(null);
  const [showManual,     setShowManual]     = useState(false);
  const [manualList,     setManualList]     = useState([]);
  const [activeTab,      setActiveTab]      = useState("overview");

  useEffect(() => { fetchROI(); }, [dateRange]);

  const fetchROI = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/feedback/campaigns/roi/overview`, authHeaders());
      const campaigns = (Array.isArray(res.data) ? res.data : []).map((c) => ({
        ...c,
        id:       c.campaignid || c.id,
        // Map DB column names to what the UI chart expects
        revenue:  Number(c.revenue_actual || 0),
        goal:     Number(c.revenue_goal   || 0),
        credits:  Number(c.credits_budget || 0),
        roi:      Number(c.roi_pct        || 0),
        clicks:   Number(c.clicked        || 0),
        sent:     Number(c.sent           || 0),
        delivered:Number(c.delivered      || 0),
        conversions: Number(c.converted   || 0),
        conversionBreakdown: c.conversion_data || {},
        ctr:      Number(c.ctr_pct        || 0),
        createdAt: c.created_at,
      }));
      // Build summary totals from campaigns
      const summary = campaigns.length ? {
        totalRevenue:    campaigns.reduce((s, c) => s + c.revenue, 0),
        totalCredits:    campaigns.reduce((s, c) => s + c.credits, 0),
        totalConversions:campaigns.reduce((s, c) => s + c.conversions, 0),
        totalSent:       campaigns.reduce((s, c) => s + c.sent, 0),
        avgROI:          campaigns.reduce((s, c) => s + c.roi, 0) / campaigns.length,
      } : null;
      setData({ campaigns, summary });
      setManualList([]);
    } catch {
      setData({ campaigns:[], summary:null });
      setManualList([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("desc"); }
  };

  const SortIcon = ({ field }) => sortField !== field
    ? <i className="fas fa-sort text-muted ms-1" style={{ fontSize: "0.7rem" }}></i>
    : sortDir === "desc" ? <FaArrowDown className="text-primary ms-1" size={10} /> : <FaArrowUp className="text-primary ms-1" size={10} />;

  const sorted = [...(data?.campaigns || [])].sort((a, b) =>
    ((a[sortField] || 0) - (b[sortField] || 0)) * (sortDir === "desc" ? -1 : 1)
  );

  const s = data?.summary;

  const roiColor = (rev, goal) => {
    if (!goal) return "text-success";
    const p = (rev / goal) * 100;
    return p >= 100 ? "text-success" : p >= 70 ? "text-warning" : "text-danger";
  };

  if (loading) return <div className="d-flex justify-content-center py-5"><Spinner animation="border" variant="primary" /></div>;

  return (
    <div>
      {/* Header */}
      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h3 className="fw-bold text-dark mb-0">
              <FaTrophy className="text-warning me-2" />ROI Dashboard
            </h3>
            <p className="text-muted mb-0 mt-1">
              Full attribution · 6 conversion types · Phone & email sales · Manual logging
            </p>
          </div>
          <div className="d-flex gap-2 align-items-center">
            <EngagementDatePicker
              startDate={dateRange.start}
              endDate={dateRange.end}
              onChange={({ start, end }) => setDateRange({ start, end })}
              align="right"
            />
            <Button variant="warning" size="sm" className="px-3" onClick={() => setShowManual(true)}>
              <FaEdit className="me-1" />Log Phone / Walk-in Sale
            </Button>
          </div>
        </div>
      </div>

      {/* ── KPI Cards ─────────────────────────────────────────────────────── */}
      <Row className="g-3 mb-4">
        {[
          { label: "Total Revenue",    value: `₹${((s?.totalRevenue||0)/1000).toFixed(1)}K`,  sub: `${s?.totalConversions||0} conversions`,          icon: <FaRupeeSign />,   c: "success" },
          { label: "Credits Cost",     value: (s?.totalCreditCost||0).toLocaleString(),         sub: "₹ equivalent spend",                             icon: <FaCoins />,       c: "warning" },
          { label: "ROI",              value: `${(s?.roi||0).toFixed(0)}%`,                    sub: "return on credit spend",                         icon: <FaTrophy />,      c: s?.roi > 1000 ? "success" : "primary" },
          { label: "Click Rate",       value: `${s?.clickRate||0}%`,                           sub: `${(s?.totalClicks||0).toLocaleString()} clicks`, icon: <FaMousePointer />,c: "info" },
          { label: "Conversion Rate",  value: `${s?.conversionRate||0}%`,                      sub: "across all types",                               icon: <FaCheckCircle />, c: "primary" },
          { label: "Rev / Send",       value: `₹${s?.avgRevenuePerSend||0}`,                  sub: "average per message",                            icon: <FaChartLine />,   c: "dark" },
        ].map((k, i) => (
          <Col xs={6} md={4} lg={2} key={i}>
            <Card className="shadow-sm border-0 h-100">
              <Card.Body className="p-3">
                <div className="d-flex align-items-center mb-2">
                  <div className={`bg-${k.c} bg-opacity-10 rounded p-2 me-2`} style={{ fontSize: "0.82rem" }}>
                    {React.cloneElement(k.icon, { className: `text-${k.c}` })}
                  </div>
                  <small className="text-muted fw-semibold text-uppercase"
                    style={{ fontSize: "0.63rem", letterSpacing: "0.04em" }}>{k.label}</small>
                </div>
                <div className={`fw-bold text-${k.c}`} style={{ fontSize: "1.35rem" }}>{k.value}</div>
                <div className="text-muted" style={{ fontSize: "0.7rem" }}>{k.sub}</div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-4" id="roi-tabs">

        {/* ── TAB 1: Conversion breakdown ──────────────────────────────── */}
        <Tab eventKey="overview" title="Conversion Breakdown">
          <Row className="g-3 mb-4">
            {CONVERSION_TYPES.map((ct) => {
              const count = s?.byType?.[ct.id] || 0;
              const total = Object.values(s?.byType || {}).reduce((a,b) => a+b, 0) || 1;
              return (
                <Col xs={6} md={4} key={ct.id}>
                  <Card className="shadow-sm border-0 h-100">
                    <Card.Body className="p-3">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <div className={`bg-${ct.color} bg-opacity-10 rounded p-2`} style={{ fontSize: "0.82rem" }}>
                          {React.cloneElement(ct.icon, { className: `text-${ct.color}` })}
                        </div>
                        <div>
                          <div className="fw-semibold" style={{ fontSize: "0.84rem" }}>{ct.label}</div>
                          <div className="d-flex align-items-center gap-1">
                            {ct.auto
                              ? <Badge bg="success" style={{ fontSize: "0.6rem" }}>Auto</Badge>
                              : <Badge bg="warning" text="dark" style={{ fontSize: "0.6rem" }}>Manual</Badge>}
                          </div>
                        </div>
                      </div>
                      <div className="fw-bold h4 mb-0">{count.toLocaleString()}</div>
                      <ProgressBar now={(count/total)*100} variant={ct.color}
                        style={{ height: 5, marginTop: 6, borderRadius: 3 }} />
                      <div className="text-muted mt-1" style={{ fontSize: "0.7rem" }}>
                        {ct.desc}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Tab>

        {/* ── TAB 2: Campaign table ─────────────────────────────────────── */}
        <Tab eventKey="campaigns" title="By Campaign">
          <Card className="shadow-sm border-0 mb-4">
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      {[
                        ["Campaign",  "ps-4", false],
                        ["Sent",      "text-center", true,  "sent"],
                        ["Clicks",    "text-center", true,  "clicks"],
                        ["Conv.",     "text-center", true,  "conversions"],
                        ["Revenue",   "text-end",    true,  "revenue"],
                        ["vs Goal",   "text-center", false],
                        ["Credits",   "text-end",    true,  "credits"],
                        ["ROI",       "text-center", true,  "revenue"],
                        ["Status",    "text-center pe-4", false],
                      ].map(([h, cls, sortable, sf]) => (
                        <th key={h}
                          className={`py-3 border-0 fw-semibold text-muted ${cls}`}
                          style={{ fontSize: "0.72rem", textTransform: "uppercase", cursor: sortable ? "pointer" : "default" }}
                          onClick={sortable ? () => toggleSort(sf) : undefined}>
                          {h}{sortable && <SortIcon field={sf} />}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((c) => {
                      const clickPct = c.sent  > 0 ? ((c.clicks/c.sent)*100).toFixed(1) : 0;
                      const convPct  = c.sent  > 0 ? ((c.conversions/c.sent)*100).toFixed(1) : 0;
                      const roi      = c.credits > 0 ? Math.round(((c.revenue - c.credits*0.4) / (c.credits*0.4)) * 100) : null;
                      const goalPct  = c.revenueGoal > 0 ? Math.min(100, Math.round((c.revenue/c.revenueGoal)*100)) : null;
                      return (
                        <tr key={c.id} className="align-middle" style={{ cursor: "pointer" }}
                          onClick={() => setSelected(selected?.id === c.id ? null : c)}>
                          <td className="ps-4">
                            <div className="fw-semibold">{EVENT_ICON[c.eventType]||"📣"} {c.name}</div>
                            <small className="text-muted">{dayjs(c.date).format("DD MMM YYYY")}</small>
                          </td>
                          <td className="text-center">{c.sent?.toLocaleString()}</td>
                          <td className="text-center">
                            <span className="text-success fw-semibold">{c.clicks?.toLocaleString()}</span>
                            <div style={{ fontSize: "0.68rem", color: "#6c757d" }}>{clickPct}%</div>
                          </td>
                          <td className="text-center">
                            <span className="text-primary fw-semibold">{c.conversions}</span>
                            <div style={{ fontSize: "0.68rem", color: "#6c757d" }}>{convPct}%</div>
                          </td>
                          <td className={`text-end fw-bold ${roiColor(c.revenue, c.revenueGoal)}`}>
                            ₹{c.revenue?.toLocaleString()}
                          </td>
                          <td className="text-center">
                            {goalPct !== null ? (
                              <div>
                                <ProgressBar now={goalPct} variant={goalPct>=100?"success":goalPct>=70?"warning":"danger"}
                                  style={{ height: 6, width: 80, margin: "0 auto 2px" }} />
                                <div style={{ fontSize: "0.68rem", color: "#6c757d" }}>{goalPct}%</div>
                              </div>
                            ) : <span className="text-muted">—</span>}
                          </td>
                          <td className="text-end text-muted">{c.credits?.toLocaleString()}</td>
                          <td className="text-center">
                            {roi !== null
                              ? <span className={`fw-bold ${roi > 0 ? "text-success" : "text-danger"}`}>
                                  {roi > 0 ? "+" : ""}{roi}%
                                </span>
                              : "—"}
                          </td>
                          <td className="text-center pe-4">
                            <Badge bg={statusVariant(c.status)} pill style={{ fontSize: "0.65rem" }}>{c.status}</Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>

          {/* Drilldown */}
          {selected && (
            <Card className="shadow-sm border-0" style={{ borderLeft: "4px solid #0d6efd" }}>
              <Card.Header className="bg-white border-bottom py-3 d-flex justify-content-between">
                <h6 className="fw-bold mb-0">{EVENT_ICON[selected.eventType]} {selected.name} — Conversion Breakdown</h6>
                <Button variant="link" size="sm" className="p-0 text-muted" onClick={() => setSelected(null)}>✕</Button>
              </Card.Header>
              <Card.Body className="p-4">
                <Row className="g-3">
                  {CONVERSION_TYPES.map((ct) => {
                    const count = selected.conversionBreakdown?.[ct.id] || 0;
                    return (
                      <Col xs={6} md={2} key={ct.id}>
                        <div className="text-center p-3 rounded border">
                          <div style={{ fontSize: "1.1rem" }}>
                            {React.cloneElement(ct.icon, { className: `text-${ct.color}` })}
                          </div>
                          <div className="fw-bold h5 mb-0 mt-1">{count}</div>
                          <div className="text-muted" style={{ fontSize: "0.72rem" }}>{ct.label}</div>
                          {!ct.auto && <Badge bg="warning" text="dark" style={{ fontSize: "0.6rem" }} className="mt-1">Manual</Badge>}
                        </div>
                      </Col>
                    );
                  })}
                </Row>
                {selected.revenueGoal > 0 && (
                  <div className="mt-4">
                    <div className="d-flex justify-content-between mb-1" style={{ fontSize: "0.84rem" }}>
                      <span>Revenue vs Goal</span>
                      <span className={`fw-bold ${roiColor(selected.revenue, selected.revenueGoal)}`}>
                        ₹{selected.revenue?.toLocaleString()} / ₹{selected.revenueGoal?.toLocaleString()}
                      </span>
                    </div>
                    <ProgressBar
                      now={Math.min(100, Math.round((selected.revenue/selected.revenueGoal)*100))}
                      variant={selected.revenue >= selected.revenueGoal ? "success" : selected.revenue >= selected.revenueGoal * 0.7 ? "warning" : "danger"}
                      style={{ height: 12, borderRadius: 6 }}
                    />
                  </div>
                )}
              </Card.Body>
            </Card>
          )}
        </Tab>

        {/* ── TAB 3: Manual conversions log ────────────────────────────── */}
        <Tab eventKey="manual" title={`Phone & Walk-in (${manualList.length})`}>
          <Card className="shadow-sm border-0">
            <Card.Header className="bg-white border-bottom py-3 d-flex justify-content-between">
              <h6 className="fw-bold mb-0">
                <FaPhone className="text-warning me-2" />Phone Sales & Walk-in Conversions
              </h6>
              <Button variant="warning" size="sm" onClick={() => setShowManual(true)}>
                <FaEdit className="me-1" />Log New Sale
              </Button>
            </Card.Header>
            <Card.Body className="p-0">
              <Table hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    {["Date","Guest","Campaign","Type","Amount","Note","Logged By"].map((h, i) => (
                      <th key={h} className={`py-3 border-0 fw-semibold text-muted ${i===0?"ps-4":""}`}
                        style={{ fontSize: "0.72rem", textTransform: "uppercase" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {manualList.map((m) => {
                    const campaign = data?.campaigns?.find((c) => c.id === m.campaignId);
                    return (
                      <tr key={m.id} className="align-middle">
                        <td className="ps-4 text-muted">{dayjs(m.date).format("DD MMM YYYY")}</td>
                        <td>
                          <div className="fw-semibold">{m.guestName}</div>
                          <div className="text-muted" style={{ fontSize: "0.72rem" }}>{m.phone}</div>
                        </td>
                        <td className="text-muted small">{campaign?.name || m.campaignId}</td>
                        <td>
                          <Badge bg={m.type === "phone_sale" ? "warning" : "info"} text="dark" style={{ fontSize: "0.68rem" }}>
                            {m.type === "phone_sale" ? "📞 Phone" : "✉️ Walk-in"}
                          </Badge>
                        </td>
                        <td className="fw-bold text-success">₹{Number(m.amount).toLocaleString()}</td>
                        <td className="text-muted small">{m.note}</td>
                        <td className="text-muted small">{m.staffName}</td>
                      </tr>
                    );
                  })}
                  {manualList.length === 0 && (
                    <tr><td colSpan={7} className="text-center py-4 text-muted">
                      No manual conversions logged yet.
                      <Button variant="link" size="sm" className="ms-1 p-0" onClick={() => setShowManual(true)}>Log one →</Button>
                    </td></tr>
                  )}
                </tbody>
              </Table>
              {manualList.length > 0 && (
                <div className="px-4 py-3 d-flex justify-content-between align-items-center border-top" style={{ background: "#f9fafb" }}>
                  <span className="text-muted small">{manualList.length} manual conversions</span>
                  <span className="fw-bold text-success">
                    Total attributed: ₹{manualList.reduce((a, m) => a + Number(m.amount), 0).toLocaleString()}
                  </span>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Manual conversion modal */}
      <ManualConversionModal
        show={showManual}
        onHide={() => setShowManual(false)}
        campaigns={data?.campaigns || []}
        onSaved={(conv) => setManualList((p) => [conv, ...p])}
      />

      <style>{`
        .card { border-radius: 12px !important; }
        .card-header { border-radius: 12px 12px 0 0 !important; }
        .page-header { margin-bottom: 1.5rem; }
      `}</style>
    </div>
  );
};

export default ROIDashboard;
